import type { BlobstoreAssetRef, NodeId } from 'web';

/** Tool-only prompt default. Present (even as `""`) means "Add new content"; absent means inherit the node. */
export interface ToolConfigPromptDefault {
  prompt: string;
}

/** Tool-only Text node default, in the Text node's own value shape. Present (even as `""`) means "Add new content". */
export interface ToolConfigTextDefault {
  string: string;
}

/** Normal asset ref. The Cloudinary URL stays readable while Blobstore is rolling out. `{ file: null }` is still Add new content. */
type StoredFileMeta = {
  width?: number;
  height?: number;
  mime?: string;
  sizeBytes?: number;
};

export type ToolConfigStoredFile = StoredFileMeta & {
  url?: string;
  blobstore?: BlobstoreAssetRef;
};

export function storedFileLocator(file: ToolConfigStoredFile): string {
  return file.blobstore?.id ?? file.url ?? '';
}

export interface ToolConfigFileDefault {
  file: ToolConfigStoredFile | null;
  type?: string;
}

export type ToolConfigInputDefault = ToolConfigPromptDefault | ToolConfigTextDefault | ToolConfigFileDefault;
export type ToolConfigOutputExample =
  | { kind: 'text'; text: string }
  | { kind: 'file'; file: ToolConfigStoredFile; type: 'image' | 'video' | 'audio' | '3D' };

export type ToolConfigFileOutputExample = Extract<ToolConfigOutputExample, { kind: 'file' }>;

export interface ToolConfigOutput {
  nodeId: NodeId;
  /** Frozen examples shown until the viewer has their own run results. */
  examples?: ToolConfigOutputExample[];
  /**
   * Every file the builder uploaded for this output, picked or not, in upload order. The picked ones
   * are also in `examples`; an unpicked one stays here so the builder can pick it again.
   */
  uploads?: ToolConfigFileOutputExample[];
}

/** The two lists one write edits together: the picked `examples` and every `upload`. */
export interface OutputExampleState {
  examples: ToolConfigOutputExample[];
  uploads: ToolConfigFileOutputExample[];
}

export function isPromptDefault(value: ToolConfigInputDefault | undefined): value is ToolConfigPromptDefault {
  return value !== undefined && 'prompt' in value;
}

export function isTextDefault(value: ToolConfigInputDefault | undefined): value is ToolConfigTextDefault {
  return value !== undefined && 'string' in value;
}

export function isFileDefault(value: ToolConfigInputDefault | undefined): value is ToolConfigFileDefault {
  return value !== undefined && 'file' in value;
}

/** Overrides only: the input list itself stays derived from the graph. Order is array position. */
export interface ToolConfigInput {
  nodeId: NodeId;
  exposed: boolean;
  /** Absent means "derive it from the graph". */
  required?: boolean;
  description?: string;
  defaultValue?: ToolConfigInputDefault;
}

/**
 * What the tool is called and looks like, held here until a `workflow_tools` row exists to own it.
 * Unlike inputs and outputs nothing derives these from the graph, so they live only in the overlay.
 */
export interface ToolConfigMetadata {
  name?: string;
  description?: string;
  /** Absent means never set. `null` means the builder removed it. */
  thumbnail?: ToolConfigStoredFile | null;
}

/**
 * The tool's declaration, stored on the workflow **version** as `recipe_versions.toolConfig`.
 * Stored configs may omit inputs; `UpdateToolConfigRequestDto` requires them on writes.
 */
export interface ToolConfig {
  version: 1;
  outputs: ToolConfigOutput[];
  inputs?: ToolConfigInput[];
  metadata?: ToolConfigMetadata;
  /**
   * The nodes that were locked when an editor first opened this workflow with the Tool tab flag on. Core
   * takes it and owns it: a config write never sets it, and only a release (an unlock, or the builder
   * exposing the node) takes ids out. Absent until then.
   */
  lockedAtConversion?: string[];
}

export type ToolConfigWithInputs = ToolConfig & Required<Pick<ToolConfig, 'inputs'>>;

export const EMPTY_TOOL_CONFIG: ToolConfigWithInputs = { version: 1, outputs: [], inputs: [] };
